import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "antd";
import type mapboxgl from "mapbox-gl";
import PageHeader from "./components/PageHeader";
import RatesDosagePanel from "./components/RatesDosagePanel";
import { RatesLoadError } from "./components/RatesLoadError";
import { StripeManualSelectionProvider } from "./components/StripeManualSelection/context";
import { PlotSelectionProvider } from "./context/PlotSelectionContext";
import SeedPlotSelectionMap from "./map/SeedPlotSelectionMap";
import useTrialBootstrap from "./hooks/useTrialBootstrap";
import { RATES_DOSAGE_STEP } from "./constants";
import { TrialMapComponent } from "./legacy";
import { Loader } from "../../shared/components/Loader";
import { ReactComponent as ArrowOutBtn } from "../../assets/images/sidebar_close.svg";
import { ReactComponent as ArrowInBtn } from "../../assets/images/sidebar_open.svg";
import "../style.less";
import "./style.less";

const StableTrialMap = memo(TrialMapComponent);

const NewRatesAndDosage = (): JSX.Element => {
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null);

  const { isLoading, isError, retry } = useTrialBootstrap();

  useEffect(() => {
    if (!isLoading && !isError) {
      setHasLoaded(true);
    }
  }, [isLoading, isError]);

  const handleToggleMapFullscreen = (): void => {
    setIsMapFullscreen((previous) => !previous);
  };

  const handleMapLoad = useCallback((map: mapboxgl.Map): void => {
    setMapInstance(map);
  }, []);

  const mapOverlays = useMemo(
    () => <SeedPlotSelectionMap map={mapInstance} />,
    [mapInstance]
  );

  return (
    <PlotSelectionProvider>
      <StripeManualSelectionProvider>
        <div className="newTrail-page new-rates-dosage-page">
          {isLoading && <Loader mask message="" />}

          <div className="newTrail-header">
            <PageHeader />

            <div
              className={`newTrail-data ${isMapFullscreen ? "map-open" : ""}`}
            >
              <div className={`left-sec ${isMapFullscreen ? "map-open" : ""}`}>
                <div className="inner-sec remove-padding">
                  <div>
                    {isError && (
                      <RatesLoadError onRetry={retry} isRetrying={isLoading} />
                    )}

                    {hasLoaded && (
                      <div className="rd-tabs-panel-container" hidden={isError}>
                        <RatesDosagePanel />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={`right-sec ${isMapFullscreen ? "map-open" : ""}`}>
                <Button
                  className="btn-map"
                  data-testid="new-rates-dosage-map-toggle"
                  onClick={handleToggleMapFullscreen}
                >
                  {isMapFullscreen ? <ArrowInBtn /> : <ArrowOutBtn />}
                </Button>

                <StableTrialMap
                  current={RATES_DOSAGE_STEP}
                  variant="rates-v2"
                  isMapFullscreen={isMapFullscreen}
                  isFertilizerTabActive={false}
                  onLoad={handleMapLoad}
                  overlays={mapOverlays}
                />
              </div>
            </div>
          </div>
        </div>
      </StripeManualSelectionProvider>
    </PlotSelectionProvider>
  );
};

export default NewRatesAndDosage;
